import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Calendar, Camera, Plus, Trash2, Edit2,
  Save, Loader2, Star, Shield, Globe, Phone, Mail, Award,
  CheckCircle, Image, FileText, DollarSign, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_OPTIONS = [
  'Interior Painting', 'Exterior Painting', 'Flooring', 'Kitchen Renovation',
  'Bathroom Renovation', 'Cabinet Refinishing', 'Countertops', 'Roofing',
  'Landscaping', 'HVAC', 'Plumbing', 'Electrical', 'General Contracting',
  'Home Staging', 'Cleaning Services', 'Real Estate Photography'
];

const CITIES = ['Toronto', 'Vancouver', 'Calgary', 'Ottawa', 'Montreal', 'Edmonton', 'Winnipeg', 'Halifax'];

const ContractorPortfolio = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [addingProject, setAddingProject] = useState(false);
  
  const [portfolio, setPortfolio] = useState({
    company_name: '',
    tagline: '',
    bio: '',
    services: [],
    service_areas: [],
    years_experience: '',
    license_number: '',
    website: '',
    phone: '',
    email: '',
    logo_url: '',
    cover_image_url: '',
    projects: []
  });
  
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    project_type: '',
    city: '',
    province_state: '',
    value: '',
    completion_date: '',
    before_images: [],
    after_images: [],
    client_testimonial: '',
    client_name: '',
    featured: false
  });

  useEffect(() => {
    fetchPortfolio();
  }, [token]);

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API}/api/contractor/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolio({
          ...portfolio,
          ...data,
          years_experience: data.years_experience?.toString() || ''
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePortfolio = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/contractor/portfolio`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...portfolio,
          years_experience: portfolio.years_experience ? parseInt(portfolio.years_experience) : null
        })
      });

      if (!response.ok) throw new Error('Failed to save');
      toast.success('Portfolio saved!');
    } catch (error) {
      toast.error('Failed to save portfolio');
    } finally {
      setSaving(false);
    }
  };

  const addProject = async () => {
    if (!newProject.title || !newProject.project_type || !newProject.city) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setAddingProject(true);
      const response = await fetch(`${API}/api/contractor/portfolio/project`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newProject,
          value: newProject.value ? parseFloat(newProject.value) : null
        })
      });

      if (!response.ok) throw new Error('Failed to add project');
      
      const data = await response.json();
      setPortfolio(prev => ({
        ...prev,
        projects: [...prev.projects, data.project]
      }));
      
      setNewProject({
        title: '', description: '', project_type: '', city: '', province_state: '',
        value: '', completion_date: '', before_images: [], after_images: [],
        client_testimonial: '', client_name: '', featured: false
      });
      setProjectDialogOpen(false);
      toast.success('Project added!');
    } catch (error) {
      toast.error('Failed to add project');
    } finally {
      setAddingProject(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    
    try {
      const response = await fetch(`${API}/api/contractor/portfolio/project/${projectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      setPortfolio(prev => ({
        ...prev,
        projects: prev.projects.filter(p => p.project_id !== projectId)
      }));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const toggleService = (service) => {
    setPortfolio(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleServiceArea = (city) => {
    setPortfolio(prev => ({
      ...prev,
      service_areas: prev.service_areas.includes(city)
        ? prev.service_areas.filter(c => c !== city)
        : [...prev.service_areas, city]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="contractor-portfolio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#FF3B30]" />
            Contractor Portfolio
          </h1>
          <p className="text-neutral-500 mt-1">Showcase your work and attract homeowners</p>
        </div>
        <Button 
          onClick={savePortfolio} 
          disabled={saving}
          className="bg-[#FF3B30] hover:bg-[#DC2626]"
          data-testid="save-portfolio-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Portfolio
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <p className="text-2xl font-bold text-neutral-900">{portfolio.projects?.length || 0}</p>
          <p className="text-sm text-neutral-500">Projects</p>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <p className="text-2xl font-bold text-neutral-900">{portfolio.services?.length || 0}</p>
          <p className="text-sm text-neutral-500">Services</p>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <p className="text-2xl font-bold text-neutral-900">{portfolio.service_areas?.length || 0}</p>
          <p className="text-sm text-neutral-500">Areas</p>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <div className="flex items-center justify-center gap-1">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <p className="text-2xl font-bold text-neutral-900">{portfolio.average_rating || '0.0'}</p>
          </div>
          <p className="text-sm text-neutral-500">Rating</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start border-b border-neutral-200 rounded-none bg-neutral-50 p-1">
            <TabsTrigger value="profile" data-testid="tab-profile">Company Profile</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Services & Areas</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects ({portfolio.projects?.length || 0})</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Company Name *
                </Label>
                <Input
                  value={portfolio.company_name}
                  onChange={(e) => setPortfolio({...portfolio, company_name: e.target.value})}
                  placeholder="Your Company Name"
                  className="mt-2"
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input
                  value={portfolio.tagline}
                  onChange={(e) => setPortfolio({...portfolio, tagline: e.target.value})}
                  placeholder="Quality work, on time, every time"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Years of Experience
                </Label>
                <Input
                  type="number"
                  value={portfolio.years_experience}
                  onChange={(e) => setPortfolio({...portfolio, years_experience: e.target.value})}
                  placeholder="10"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  License Number
                </Label>
                <Input
                  value={portfolio.license_number}
                  onChange={(e) => setPortfolio({...portfolio, license_number: e.target.value})}
                  placeholder="Optional"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Website
                </Label>
                <Input
                  value={portfolio.website}
                  onChange={(e) => setPortfolio({...portfolio, website: e.target.value})}
                  placeholder="https://yourwebsite.com"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone
                </Label>
                <Input
                  value={portfolio.phone}
                  onChange={(e) => setPortfolio({...portfolio, phone: e.target.value})}
                  placeholder="+1 (416) 555-0000"
                  className="mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Bio / About
                </Label>
                <Textarea
                  value={portfolio.bio}
                  onChange={(e) => setPortfolio({...portfolio, bio: e.target.value})}
                  placeholder="Tell homeowners about your company, experience, and what makes you different..."
                  rows={4}
                  className="mt-2"
                />
              </div>
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="p-6 space-y-6">
            <div>
              <Label className="text-lg font-semibold mb-4 block">Services Offered</Label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map((service) => (
                  <Badge
                    key={service}
                    variant={portfolio.services?.includes(service) ? "default" : "outline"}
                    className={`cursor-pointer px-3 py-1.5 ${
                      portfolio.services?.includes(service) 
                        ? 'bg-[#FF3B30] hover:bg-[#DC2626]' 
                        : 'hover:bg-neutral-100'
                    }`}
                    onClick={() => toggleService(service)}
                  >
                    {service}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-4 block">Service Areas</Label>
              <div className="flex flex-wrap gap-2">
                {CITIES.map((city) => (
                  <Badge
                    key={city}
                    variant={portfolio.service_areas?.includes(city) ? "default" : "outline"}
                    className={`cursor-pointer px-3 py-1.5 ${
                      portfolio.service_areas?.includes(city) 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-neutral-100'
                    }`}
                    onClick={() => toggleServiceArea(city)}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    {city}
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">Your Projects</h3>
              <Button onClick={() => setProjectDialogOpen(true)} data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
            </div>

            {portfolio.projects?.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">No projects yet</p>
                <Button onClick={() => setProjectDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Project
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {portfolio.projects?.map((project) => (
                  <div key={project.project_id} className="border border-neutral-200 rounded-xl overflow-hidden group">
                    <div className="relative h-40 bg-neutral-100">
                      {project.after_images?.[0] ? (
                        <img src={project.after_images[0]} alt={project.title} className="w-full h-full object-cover" />
                      ) : project.before_images?.[0] ? (
                        <img src={project.before_images[0]} alt={project.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-neutral-300" />
                        </div>
                      )}
                      {project.featured && (
                        <Badge className="absolute top-2 left-2 bg-yellow-500">Featured</Badge>
                      )}
                      <button
                        onClick={() => deleteProject(project.project_id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-neutral-900">{project.title}</h4>
                      <p className="text-sm text-neutral-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {project.city}
                        {project.value && (
                          <span className="ml-2 text-green-600">
                            ${project.value.toLocaleString()}
                          </span>
                        )}
                      </p>
                      {project.client_testimonial && (
                        <p className="text-sm text-neutral-600 mt-2 italic line-clamp-2">
                          "{project.client_testimonial}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Project Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Project</DialogTitle>
            <DialogDescription>Showcase your completed work</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Project Title *</Label>
              <Input
                value={newProject.title}
                onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                placeholder="Kitchen Renovation in Toronto"
                className="mt-2"
                data-testid="project-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Type *</Label>
                <Select value={newProject.project_type} onValueChange={(v) => setNewProject({...newProject, project_type: v})}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>City *</Label>
                <Select value={newProject.city} onValueChange={(v) => setNewProject({...newProject, city: v})}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Value ($)</Label>
                <Input
                  type="number"
                  value={newProject.value}
                  onChange={(e) => setNewProject({...newProject, value: e.target.value})}
                  placeholder="5000"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Completion Date</Label>
                <Input
                  type="date"
                  value={newProject.completion_date}
                  onChange={(e) => setNewProject({...newProject, completion_date: e.target.value})}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="Describe the project scope and work completed..."
                rows={3}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Client Testimonial</Label>
              <Textarea
                value={newProject.client_testimonial}
                onChange={(e) => setNewProject({...newProject, client_testimonial: e.target.value})}
                placeholder="What the client said about your work..."
                rows={2}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Client Name</Label>
              <Input
                value={newProject.client_name}
                onChange={(e) => setNewProject({...newProject, client_name: e.target.value})}
                placeholder="John D. (optional)"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Before Image URL</Label>
              <Input
                value={newProject.before_images[0] || ''}
                onChange={(e) => setNewProject({...newProject, before_images: e.target.value ? [e.target.value] : []})}
                placeholder="https://..."
                className="mt-2"
              />
            </div>

            <div>
              <Label>After Image URL</Label>
              <Input
                value={newProject.after_images[0] || ''}
                onChange={(e) => setNewProject({...newProject, after_images: e.target.value ? [e.target.value] : []})}
                placeholder="https://..."
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={addProject} 
              disabled={addingProject}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
              data-testid="submit-project-btn"
            >
              {addingProject ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorPortfolio;
